/**
 * bridge/search_controller.js — web side of the "search" Hotwire Native bridge.
 *
 * Stimulus identifier: `bridge--search` (esbuild-rails derives `--` from the
 * bridge/ subdir). iOS counterpart: native/ios/Chippie/Bridge/SearchComponent.swift
 * (`class var name == "search"`). On a Hotwire Native iOS shell, connecting sends
 * "connect" to native, which installs a UISearchController and replies with the
 * live query text on every keystroke. Each reply is re-dispatched (debounced) as a
 * `bridge--search:queried` DOM event for page controllers to filter against:
 *
 *   <div data-controller="bridge--search" data-bridge--search-placeholder-value="Search jobs"></div>
 *   <div data-action="bridge--search:queried@window->jobs#filter"></div>  // event.detail.query
 *
 * BridgeComponent gates connect() on the Hotwire Native UA token, so on PWA / web
 * desktop / Android (web-rendered, ios-chrome tier) this controller never connects
 * and is a harmless no-op — no native search field appears there.
 *
 * Adapted from Joe Masilotti's canonical Search bridge component
 * (github.com/joemasilotti/bridge-components, web/controllers/search_controller.js),
 * with a debounce added on dispatch per the Chippie integration contract.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

const DEBOUNCE_MS = 200;

export default class extends BridgeComponent {
  static component = "search";
  static values = { placeholder: String };

  connect() {
    super.connect();

    this.send("connect", { placeholder: this.placeholderValue }, (message) => {
      const query = message.data.query;
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => {
        this.dispatch("queried", { detail: { query } });
      }, DEBOUNCE_MS);
    });
  }

  disconnect() {
    clearTimeout(this._debounce);
    super.disconnect();
  }
}
